import { useState } from 'react';
import { createPortal } from 'react-dom';
import OmButton from '../ui/OmButton';
import { useDispatch, useSelector } from 'react-redux';
import { validateDocumentStamp, overrideStampValidation } from '../../features/documents/documentSlice';
import { selectUserRoles } from '../../features/auth/authSlice';
import { getDocumentTypeLabel } from '../../features/documents/documentService';

function StampValidationModal({ document, onClose, onSuccess }) {
  const dispatch = useDispatch();
  const userRoles = useSelector(selectUserRoles);
  const [isValid, setIsValid] = useState(null);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const isApprover = userRoles.includes('Approver');
  const canOverride = isApprover && document.stampValidatedAt !== null;

  // Character limit for notes
  const NOTES_MAX_LENGTH = 500;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isValid === null) {
      setError('Please select whether the stamp is valid or invalid');
      return;
    }

    if (!notes.trim()) {
      setError('Please provide validation notes');
      return;
    }

    if (notes.length > NOTES_MAX_LENGTH) {
      setError(`Notes cannot exceed ${NOTES_MAX_LENGTH} characters`);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      if (canOverride) {
        await dispatch(overrideStampValidation({
          documentId: document.id,
          isValid,
          notes: notes.trim()
        })).unwrap();
      } else {
        await dispatch(validateDocumentStamp({
          documentId: document.id,
          isValid,
          notes: notes.trim()
        })).unwrap();
      }
      
      onSuccess();
    } catch (err) {
      setError(err.message || 'Failed to validate stamp. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto bg-carbon-black bg-opacity-60 pointer-events-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen p-4">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity" 
          onClick={handleClose}
          aria-hidden="true"
        />

        {/* Modal panel */}
        <div className="relative top-12 mx-auto p-0 bg-crystal-white text-left overflow-hidden shadow-depth border border-steel/20 rounded-lg w-full sm:max-w-lg">
          {/* Header */}
          <div className="bg-deep-jungle px-6 py-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 bg-fresh-leaf/20">
                <svg className="h-6 w-6 text-crystal-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <h3 className="text-lg font-display font-semibold text-crystal-white" id="modal-title">
                  {canOverride ? 'Override Stamp Validation' : 'Validate Hospital Stamp'}
                </h3>
              </div>
            </div>
          </div>

          {/* Document Info */}
          <div className="px-6 py-4 bg-silver-mist border-b border-steel/10">
            <div className="grid grid-cols-1 gap-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-graphite">Document:</span>
                <span className="text-sm text-deep-jungle font-semibold">{document.name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-graphite">Type:</span>
                <span className="text-sm text-deep-jungle font-semibold">{getDocumentTypeLabel(document.type)}</span>
              </div>
              {document.stampValidationStatus && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-graphite">Current Status:</span>
                  <span className={`badge ${document.stampValidationStatus.toLowerCase() === 'valid' ? 'badge-success' : 'badge-error'}`}>
                    {document.stampValidationStatus.toLowerCase()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6">
            {/* Validation Decision */}
            <div className="mb-6">
              <fieldset>
                <legend className="text-sm font-semibold text-deep-jungle mb-4 uppercase tracking-wide">
                  Stamp Validation Decision *
                </legend>
                <div className="space-y-3">
                  <label className="flex items-start p-4 border border-steel/20 hover:border-fresh-leaf/50 transition-colors cursor-pointer focus-within:border-fresh-leaf focus-within:bg-fresh-leaf/5">
                    <input
                      type="radio"
                      name="validation"
                      value="valid"
                      checked={isValid === true}
                      onChange={() => setIsValid(true)}
                      className="h-4 w-4 text-fresh-leaf border-steel focus:ring-fresh-leaf focus:ring-2 mt-1"
                      disabled={isSubmitting}
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex items-center">
                        <svg className="h-4 w-4 text-fresh-leaf mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-medium text-deep-jungle">Stamp is Valid</span>
                      </div>
                      <p className="text-xs text-steel mt-1">Document has proper hospital/medical facility stamp</p>
                    </div>
                  </label>
                  <label className="flex items-start p-4 border border-steel/20 hover:border-error/50 transition-colors cursor-pointer focus-within:border-error focus-within:bg-error/5">
                    <input
                      type="radio"
                      name="validation"
                      value="invalid"
                      checked={isValid === false}
                      onChange={() => setIsValid(false)}
                      className="h-4 w-4 text-error border-steel focus:ring-error focus:ring-2 mt-1"
                      disabled={isSubmitting}
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex items-center">
                        <svg className="h-4 w-4 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                        <span className="text-sm font-medium text-deep-jungle">Stamp is Invalid</span>
                      </div>
                      <p className="text-xs text-steel mt-1">Missing or improper stamp</p>
                    </div>
                  </label>
                </div>
              </fieldset>
            </div>

            {/* Validation Notes */}
            <div className="mb-6">
              <label htmlFor="validation-notes" className="block text-sm font-semibold text-deep-jungle mb-3 uppercase tracking-wide">
                Validation Notes *
              </label>
              <textarea
                id="validation-notes"
                name="notes"
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Provide detailed notes about the stamp validation decision..."
                className="block w-full border border-steel/30 px-4 py-3 text-sm text-graphite focus:outline-none focus:border-fresh-leaf focus:ring-2 focus:ring-fresh-leaf/20 transition-colors bg-crystal-white"
                disabled={isSubmitting}
                maxLength={NOTES_MAX_LENGTH}
                required
              />
              <div className="flex justify-between items-center mt-2">
                <p className="text-xs text-steel">
                  {isValid === false ? 'Required: Explain why the stamp is invalid or missing' : 'Describe the stamp verification details'}
                </p>
                <span className={`text-xs font-mono ${notes.length > NOTES_MAX_LENGTH * 0.9 ? 'text-error' : 'text-steel'}`}>
                  {notes.length}/{NOTES_MAX_LENGTH}
                </span>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-error/5 border-l-4 border-error">
                <div className="flex">
                  <svg className="h-5 w-5 text-error flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="ml-3 text-sm text-error font-medium">{error}</p>
                </div>
              </div>
            )}

            {/* Override Warning */}
            {canOverride && (
              <div className="mb-6 p-4 bg-warning/5 border-l-4 border-warning">
                <div className="flex">
                  <svg className="h-5 w-5 text-warning flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="ml-3">
                    <h4 className="text-sm font-semibold text-warning uppercase tracking-wide">Approver Override</h4>
                    <p className="text-sm text-graphite mt-1">
                      This action will override the previous validation decision and will be logged in the audit trail.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons (OM Design System) */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <OmButton
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                buttonType="outlined-button"
                buttonSize="large"
              >
                Cancel
              </OmButton>
              <OmButton
                type="submit"
                disabled={isSubmitting || isValid === null || !notes.trim()}
                buttonType="filled-button"
                buttonSize="large"
              >
                {isSubmitting ? (canOverride ? 'Overriding...' : 'Validating...') : (canOverride ? 'Override Validation' : 'Submit Validation')}
              </OmButton>
            </div>
          </form>
        </div>
      </div>
    </div>,
    window.document && window.document.body ? window.document.body : null
  );
}

export default StampValidationModal;
